import type { CountryCode } from 'libphonenumber-js'
import { AsYouType, getCountryCallingCode } from 'libphonenumber-js'
import { NormalizedPhoneNumber } from './normalized-phone-number'

/**
 * Validates a phone number is a valid E.164 number
 * @param value - The phone number to validate
 * @param country - The country to use for validation. Defaults to US
 */
export function validatePhoneNumber(value?: string | null, country = 'US'): boolean {
  // parsePhoneNumber throws an error on empty values and values with length 1
  if (!value || value.length <= 1) return false
  return new NormalizedPhoneNumber(value, country).valid
}

/**
 * Formats a phone number to E.164 format (ex: +15103836075)
 * @param value - The phone number to format
 * @param country - The country to use for formatting. Defaults to US
 */
export function formatPhoneNumberIso(number: string, country = 'US'): string {
  return new NormalizedPhoneNumber(number, country).iso
}

/**
 * Formats a phone number to local format (ex: (510) 383-6075)
 * @param value - The phone number to format
 * @param country - The country to use for formatting. Defaults to US
 */
export function formatPhoneNumberLocal(number: string, country = 'US'): string {
  return new NormalizedPhoneNumber(number, country).local
}

/**
 * Formats a phone number to local as the user types
 * @param value - The phone number to format
 * @param country - The country to use for formatting. Defaults to US
 */
export function formatPhoneNumberOnChange(value: string, country = 'US'): string {
  return new AsYouType(country as CountryCode).input(value)
}

/**
 * Returns Calling Code for a phone number (ex: +1)
 * @param locale - The locale to use for formatting. Defaults to en-US
 */
export function getCallingCode(locale = 'en-US'): string {
  const [, country] = locale.split('-')

  return `+${getCountryCallingCode(country as CountryCode)}`
}
